import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Agreement to Terms</CardTitle>
            <CardDescription>
              By accessing and using TechTrustITSolutions video conferencing service, you agree to be bound by these Terms of Service
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <h3 className="text-lg font-semibold mb-2">1. Acceptance of Terms</h3>
              <p className="text-muted-foreground">
                By accessing or using our video conferencing service ("Service"), you agree to comply with and be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">2. Description of Service</h3>
              <p className="text-muted-foreground">
                TechTrustITSolutions provides real-time video conferencing, screen sharing, chat messaging, and related collaboration tools. We reserve the right to modify, suspend, or discontinue any part of the Service at any time.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">3. User Accounts</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You must provide accurate and complete information when creating an account</li>
                <li>You are responsible for maintaining the security of your account credentials</li>
                <li>You must not share your account with others</li>
                <li>You must notify us immediately of any unauthorized account access</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">4. Acceptable Use</h3>
              <p className="text-muted-foreground mb-2">You agree NOT to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Transmit viruses, malware, or harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the Service to spam or send unsolicited communications</li>
                <li>Record meetings without proper consent from all participants</li>
                <li>Share inappropriate, offensive, or illegal content</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">5. Content and Intellectual Property</h3>
              <p className="text-muted-foreground">
                You retain ownership of content you create or share through the Service. However, you grant us a license to use, store, and process this content to provide the Service. All Service features, design, and functionality are owned by TechTrustITSolutions and protected by copyright and other intellectual property laws.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">6. Recording and Transcription</h3>
              <p className="text-muted-foreground">
                Meeting hosts can record sessions and enable transcription services. All participants will be notified when recording begins. By participating in a recorded meeting, you consent to being recorded. Hosts are responsible for obtaining necessary consents and complying with applicable laws.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">7. Privacy and Data Protection</h3>
              <p className="text-muted-foreground">
                Your use of the Service is subject to our Privacy Policy, which describes how we collect, use, and protect your personal information. We comply with applicable data protection laws including GDPR and CCPA.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">8. Payment and Subscriptions</h3>
              <p className="text-muted-foreground">
                Some features may require payment. You agree to provide accurate billing information and authorize us to charge your payment method. Subscriptions auto-renew unless cancelled. Refunds are provided according to our refund policy.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">9. Service Availability</h3>
              <p className="text-muted-foreground">
                While we strive for 99.9% uptime, we do not guarantee uninterrupted service. We may perform maintenance, updates, or experience unexpected downtime. We are not liable for any losses resulting from service interruptions.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">10. Limitation of Liability</h3>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law, TechTrustITSolutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service. Our total liability shall not exceed the amount you paid us in the past 12 months.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">11. Indemnification</h3>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless TechTrustITSolutions from any claims, damages, or expenses arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">12. Termination</h3>
              <p className="text-muted-foreground">
                We may suspend or terminate your account at any time for violations of these Terms. You may terminate your account at any time by contacting support. Upon termination, your right to use the Service ceases immediately.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">13. Changes to Terms</h3>
              <p className="text-muted-foreground">
                We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or in-app notification. Continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">14. Governing Law</h3>
              <p className="text-muted-foreground">
                These Terms are governed by the laws of India. Any disputes shall be resolved in the courts of India.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">15. Contact Information</h3>
              <p className="text-muted-foreground">
                For questions about these Terms, please contact us at:
              </p>
              <ul className="list-none pl-0 text-muted-foreground mt-2">
                <li>Company: MeetHub by TechTrustITSolutions</li>
                <li>Email: support@meethub.com</li>
                <li>Phone: +91-82009-10548</li>
                <li>Website: https://www.techtrustitsolutions.com/</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
